/**
 * Labstep - Table Helper Functions
 *
 * Utility functions for table operations
 */

import type { Node as ProsemirrorNode } from 'prosemirror-model';
import { TextSelection } from 'prosemirror-state';
import { TableMap } from 'prosemirror-tables';
import type { EditorView } from 'prosemirror-view';

// Helper functions to check if rows/columns are empty
export const isRowEmpty = (
  map: TableMap,
  tableNode: ProsemirrorNode,
  rowIndex: number,
): boolean => {
  for (let col = 0; col < map.width; col++) {
    const cellIndex = map.map[rowIndex * map.width + col];
    const cellNode = tableNode.nodeAt(cellIndex);
    if (cellNode && cellNode.textContent.trim() !== '') {
      return false;
    }
  }
  return true;
};

export const isColumnEmpty = (
  map: TableMap,
  tableNode: ProsemirrorNode,
  colIndex: number,
): boolean => {
  for (let row = 0; row < map.height; row++) {
    const cellIndex = map.map[row * map.width + colIndex];
    const cellNode = tableNode.nodeAt(cellIndex);
    if (cellNode && cellNode.textContent.trim() !== '') {
      return false;
    }
  }
  return true;
};

// Types for handle positioning
export interface HandlePosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface HoveredCell {
  row: number;
  col: number;
}

export interface MousePosition {
  x: number;
  y: number;
}

// Table position calculation utilities
export const getPositionsForTable = (
  table: Element,
): {
  rows: HandlePosition[];
  cols: HandlePosition[];
} => {
  const rows: HandlePosition[] = [];
  const cols: HandlePosition[] = [];

  // Get row positions
  const tableRows = table.querySelectorAll('tr');
  tableRows.forEach((row: Element) => {
    const rect = row.getBoundingClientRect();
    rows.push({
      top: rect.top,
      left: rect.left - 25, // Position handle to the left of the row
      width: 20,
      height: rect.height,
    });
  });

  // Get column positions
  const firstRow = tableRows[0];
  if (firstRow) {
    const cells = firstRow.querySelectorAll('td, th');
    cells.forEach((cell: Element) => {
      const rect = cell.getBoundingClientRect();
      cols.push({
        top: rect.top - 25, // Position handle above the column
        left: rect.left,
        width: rect.width,
        height: 20,
      });
    });
  }

  return { rows, cols };
};

// Find target table based on mouse position and hovered cell
export const findTargetTable = (
  editorElement: Element,
  lastMousePosition: MousePosition | null,
  hoveredCell: HoveredCell,
): Element | null => {
  const allTables = editorElement.querySelectorAll('table');

  if (allTables.length === 0) {
    return null;
  }

  // If there's only one table, use it
  if (allTables.length === 1) {
    return allTables[0] as Element;
  }

  let targetTable: Element | null = null;

  if (lastMousePosition) {
    // Check if mouse is inside any table's extended area (including handle zones)
    for (let i = 0; i < allTables.length; i++) {
      const table = allTables[i] as Element;
      const tableRect = table.getBoundingClientRect();

      // Extend the bounding box to include handle areas
      const extendedRect = {
        left: tableRect.left - 30, // 30px left for row handles
        right: tableRect.right + 10, // Small buffer on right
        top: tableRect.top - 30, // 30px up for column handles
        bottom: tableRect.bottom + 10, // Small buffer on bottom
      };

      if (
        lastMousePosition.x >= extendedRect.left &&
        lastMousePosition.x <= extendedRect.right &&
        lastMousePosition.y >= extendedRect.top &&
        lastMousePosition.y <= extendedRect.bottom
      ) {
        targetTable = table;
        break;
      }
    }
  }

  // Fallback: use structural matching if mouse position doesn't work
  if (!targetTable) {
    for (let i = 0; i < allTables.length; i++) {
      const table = allTables[i] as Element;
      const tableRows = table.querySelectorAll('tr');

      if (tableRows.length > hoveredCell.row) {
        const targetRow = tableRows[hoveredCell.row];
        if (targetRow) {
          const cells = targetRow.querySelectorAll('td, th');
          if (cells.length > hoveredCell.col) {
            targetTable = table;
            break;
          }
        }
      }
    }
  }

  // Final fallback to first table
  if (!targetTable && allTables.length > 0) {
    targetTable = allTables[0] as Element;
  }

  return targetTable;
};

// Calculate edge handle positions
export const calculateEdgeHandlePositions = (
  hoveredCell: HoveredCell,
  rows: HandlePosition[],
  cols: HandlePosition[],
): {
  bottomEdge: HandlePosition | null;
  rightEdge: HandlePosition | null;
} => {
  if (rows.length === 0 || cols.length === 0) {
    return { bottomEdge: null, rightEdge: null };
  }

  // Bottom edge handle - appears when hovering over last row
  const isLastRow = hoveredCell.row === rows.length - 1;
  const bottomEdge = isLastRow
    ? {
        top:
          rows[rows.length - 1].top +
          rows[rows.length - 1].height +
          2,
        left: cols[0].left,
        width: cols.reduce((total, col) => total + col.width, 0),
        height: 20,
      }
    : null;

  // Right edge handle - appears when hovering over last column
  const isLastCol = hoveredCell.col === cols.length - 1;
  const rightEdge = isLastCol
    ? {
        top: rows[0].top,
        left:
          cols[cols.length - 1].left +
          cols[cols.length - 1].width +
          2,
        width: 20,
        height: rows.reduce((total, row) => total + row.height, 0),
      }
    : null;

  return { bottomEdge, rightEdge };
};

// Cell selection utility
export const selectCellInTarget = (
  view: EditorView,
  tableNode: ProsemirrorNode,
  tablePos: number,
  targetRow: number,
  targetCol: number,
): boolean => {
  try {
    const { state, dispatch } = view;
    const map = TableMap.get(tableNode);

    // Find a valid cell in the target row/column
    let cellIndex;
    if (targetRow < map.height && targetCol < map.width) {
      cellIndex = map.map[targetRow * map.width + targetCol];
    } else {
      // Fallback: use first cell if target is out of bounds
      cellIndex = map.map[0];
    }

    const cellPos = tablePos + cellIndex;

    // Create a text selection inside the cell
    const $cell = state.doc.resolve(cellPos + 1); // +1 to get inside the cell

    const tr = state.tr.setSelection(new TextSelection($cell, $cell));

    dispatch(tr);
    return true;
  } catch (error) {
    console.error(
      'selectCellInTarget: Error selecting target cell:',
      error,
    );
    return false;
  }
};

// Drag calculation utilities
export const calculateDragDistance = (
  startPos: MousePosition,
  currentPos: MousePosition,
  type: 'bottom' | 'right',
): {
  distance: number;
  isNegativeDirection: boolean;
} => {
  const deltaY = currentPos.y - startPos.y;
  const deltaX = currentPos.x - startPos.x;

  const isNegativeDirection =
    type === 'bottom' ? deltaY < 0 : deltaX < 0;
  const distance = Math.abs(type === 'bottom' ? deltaY : deltaX);

  return { distance, isNegativeDirection };
};

export const calculateItemsToAddOrDelete = (
  distance: number,
  type: 'bottom' | 'right',
  isNegativeDirection: boolean,
): number => {
  if (isNegativeDirection) {
    // Dragging backward - calculate items to delete
    return type === 'bottom'
      ? Math.floor(distance / 25) // ~25px per row
      : Math.floor(distance / 100); // ~100px per column
  } else {
    // Dragging forward - calculate items to add
    return type === 'bottom'
      ? Math.floor(distance / 25) // ~25px per row
      : Math.floor(distance / 100); // ~100px per column
  }
};

// Drag state utilities
export interface DragState {
  isDragging: boolean;
  type: 'row' | 'column' | 'bottom' | 'right';
  dragIndex?: number;
  startPos: MousePosition;
  currentPos: MousePosition;
  addedCount?: number;
}

export const processDragMovement = (
  dragState: DragState,
  newPos: MousePosition,
): {
  updatedDragState: DragState;
  shouldAddItems: boolean;
  shouldDeleteItems: boolean;
  itemsToProcess: number;
} => {
  if (dragState.type !== 'bottom' && dragState.type !== 'right') {
    return {
      updatedDragState: { ...dragState, currentPos: newPos },
      shouldAddItems: false,
      shouldDeleteItems: false,
      itemsToProcess: 0,
    };
  }

  const { distance, isNegativeDirection } = calculateDragDistance(
    dragState.startPos,
    newPos,
    dragState.type,
  );

  if (isNegativeDirection) {
    // Dragging backward - delete empty rows/columns
    const itemsToDelete = calculateItemsToAddOrDelete(
      distance,
      dragState.type,
      isNegativeDirection,
    );

    const currentDeletedCount = Math.abs(dragState.addedCount || 0);

    if (itemsToDelete > currentDeletedCount) {
      const itemsToDeleteNow = itemsToDelete - currentDeletedCount;

      return {
        updatedDragState: {
          ...dragState,
          currentPos: newPos,
          addedCount: -itemsToDelete,
        },
        shouldAddItems: false,
        shouldDeleteItems: true,
        itemsToProcess: itemsToDeleteNow,
      };
    }

    return {
      updatedDragState: { ...dragState, currentPos: newPos },
      shouldAddItems: false,
      shouldDeleteItems: false,
      itemsToProcess: 0,
    };
  } else {
    // Dragging forward - add rows/columns
    const targetCount = calculateItemsToAddOrDelete(
      distance,
      dragState.type,
      isNegativeDirection,
    );

    const currentAddedCount = Math.max(0, dragState.addedCount || 0);

    if (targetCount > currentAddedCount) {
      const itemsToAdd = targetCount - currentAddedCount;

      return {
        updatedDragState: {
          ...dragState,
          currentPos: newPos,
          addedCount: targetCount,
        },
        shouldAddItems: true,
        shouldDeleteItems: false,
        itemsToProcess: itemsToAdd,
      };
    }

    return {
      updatedDragState: { ...dragState, currentPos: newPos },
      shouldAddItems: false,
      shouldDeleteItems: false,
      itemsToProcess: 0,
    };
  }
};
